<script>
  import Page from '@components/page'
  import Block from '@components/general/block'
  import Table from '@components/table'
  import BoxedText from '@components/general/boxed-text.svelte'
  import Button from '@components/buttons/button.svelte'
  import { page } from '@inertiajs/svelte'
  import { CreditCard, Crown, Clock } from 'lucide-svelte'
  import { formatDateText, formatDateTime } from '@lib/utils/date'
  import { formatMoney } from '@lib/utils'

  let { memberships = [], invoice_requests = [], payments = [] } = $props()

  let user = $derived($page.props.user)
  let activeMembership = $derived(memberships.find(m => m.grants_access))
  let pastMemberships = $derived(memberships.filter(m => !m.grants_access))

  let links = [{ text: "Profile", href: "/settings/profile" }]

  let membershipStatusVariant = { active: 'success', inactive: 'default', canceled: 'error' }
  let invoiceStatusVariant = { pending: 'warning', approved: 'success', rejected: 'error' }
  let paymentStatusVariant = { succeeded: 'success', processing: 'warning', requires_payment_method: 'error', canceled: 'error' }
</script>

<Page.Layout title="Membership">

  <Page.Navigation title="Membership" Icon={CreditCard} {links} />

  <Page.Titles title="Membership" subtitle="View your membership and billing history" />

  <Page.Content>

    {#snippet main()}

      <!-- Current membership -->
      <Block.Element label="Membership" title={activeMembership ? "Active membership" : "No active membership"}>
        {#if activeMembership}
          <Block.Items>
            <Block.Item label="Programme" value={activeMembership.programme_name} />
            <Block.Item label="Status">
              <BoxedText variant="success" size="xs" text="Active" />
            </Block.Item>
            <Block.Item label="Valid until" value={formatDateText(activeMembership.entitlement_end_date)} />
            <Block.Item label="Provider">
              <BoxedText variant="default" size="xs" text={activeMembership.provider} />
            </Block.Item>
          </Block.Items>
        {:else if user.has_pending_invoice_request}
          <div class="flex items-center gap-2 text-amber-600 mb-3">
            <Clock size={16} />
            <span class="text-sm font-medium">Invoice request pending</span>
          </div>
          <p class="text-gray-500 text-sm mt-1">Your invoice request is being reviewed. You'll receive an email once it's approved.</p>
        {:else}
          <p class="text-gray-500 text-sm">You don't have an active membership.</p>
          <Button variant="primary" path="/register" text="Get membership" Icon={Crown} class="mt-3" />
        {/if}
      </Block.Element>

      <!-- Membership history -->
      {#if pastMemberships.length > 0}
        <Block.Element label="History" title="Past memberships">
          <Block.Table>
            <Table.Head>
              <Table.Header width="30%">Programme</Table.Header>
              <Table.Header width="25%">Valid until</Table.Header>
              <Table.Header width="20%" align="center">Provider</Table.Header>
              <Table.Header width="25%" align="center">Status</Table.Header>
            </Table.Head>
            <Table.Body>
              {#each pastMemberships as membership (membership.id)}
                <Table.Row>
                  <Table.Cell width="30%">{membership.programme_name}</Table.Cell>
                  <Table.Cell width="25%">{formatDateText(membership.entitlement_end_date)}</Table.Cell>
                  <Table.Cell width="20%" align="center">
                    <BoxedText variant="default" size="xs" text={membership.provider} />
                  </Table.Cell>
                  <Table.Cell width="25%" align="center">
                    <BoxedText variant={membershipStatusVariant[membership.status] || 'default'} size="xs" text={membership.status} />
                  </Table.Cell>
                </Table.Row>
              {/each}
            </Table.Body>
          </Block.Table>
        </Block.Element>
      {/if}

    {/snippet}

    {#snippet aside()}

      <!-- Invoice requests -->
      {#if invoice_requests.length > 0}
        <Block.Element label="Invoices" title="Invoice requests">
          <Block.Items>
            {#each invoice_requests as request (request.id)}
              <div class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p class="text-sm text-gray-700">{request.offer_name}</p>
                  <p class="text-xs text-gray-400">{formatDateText(request.created_at)}</p>
                </div>
                <BoxedText variant={invoiceStatusVariant[request.status] || 'default'} size="xs" text={request.status} />
              </div>
            {/each}
          </Block.Items>
        </Block.Element>
      {/if}

    {/snippet}

  </Page.Content>

  <!-- Payments -->
  {#if payments.length > 0}
    <Page.Content>
      {#snippet children()}
        <Block.Element label="Billing" title="Payment history">
          <Block.Table>
            <Table.Head>
              <Table.Header width="25%">Date</Table.Header>
              <Table.Header width="25%">Amount</Table.Header>
              <Table.Header width="25%">Description</Table.Header>
              <Table.Header width="25%" align="center">Status</Table.Header>
            </Table.Head>
            <Table.Body>
              {#each payments as payment (payment.id)}
                <Table.Row>
                  <Table.Cell width="25%">{formatDateTime(payment.created)}</Table.Cell>
                  <Table.Cell width="25%">{formatMoney(payment.amount)}</Table.Cell>
                  <Table.Cell width="25%">{payment.description || '—'}</Table.Cell>
                  <Table.Cell width="25%" align="center">
                    <BoxedText variant={paymentStatusVariant[payment.status] || 'default'} size="xs" text={payment.status} />
                  </Table.Cell>
                </Table.Row>
              {/each}
            </Table.Body>
          </Block.Table>
        </Block.Element>
      {/snippet}
    </Page.Content>
  {/if}

</Page.Layout>
